import { get } from 'lodash-es';
import { createSelector } from 'reselect';

import { emptyArray, emptyObject } from 'ui-components/default-props/empty';

import type {
  SsConnectPrintJob,
  SsConnectPrinter,
  SsConnectPrinterGroup,
  SsConnectScale,
  SsConnectScaleGroup,
  SsConnectState,
  SsConnectWorkstation,
} from './ss-connect.types';
import type { RootState } from 'store/root-state';

export const selectScales = (state: SsConnectState): SsConnectScaleGroup[] => get(state, 'scales', emptyArray as SsConnectScaleGroup[]);
export const selectDefaultScale = (state: SsConnectState): SsConnectScale | undefined => get(state, 'defaultScale');
export const selectPrinters = (state: SsConnectState): SsConnectPrinterGroup[] =>
  get(state, 'printers', emptyArray as SsConnectPrinterGroup[]);
export const selectPrintJobs = (state: SsConnectState): SsConnectPrintJob[] => get(state, 'printJobs', emptyArray as SsConnectPrintJob[]);
export const selectWorkstations = (state: SsConnectState): SsConnectWorkstation[] =>
  get(state, 'workstations', emptyArray as SsConnectWorkstation[]);

export const selectFlattenedPrinters: (state: SsConnectState) => SsConnectPrinter[] = createSelector(
  selectPrinters,
  (printers): SsConnectPrinter[] => {
    const flattened = printers.flatMap((group) => group.printers);
    return flattened.length === 0 ? (emptyArray as SsConnectPrinter[]) : flattened;
  },
);

export const selectHasConnect: (state: SsConnectState) => boolean = createSelector(selectWorkstations, (workstations) =>
  workstations.some((workstation) => workstation.isOnline),
);

export const selectWorkstationById = (state: SsConnectState, { workstationId }: { workstationId: string }): SsConnectWorkstation => {
  const workstations = selectWorkstations(state);
  return workstations.find((ws) => ws.workstationId === workstationId) || (emptyObject as SsConnectWorkstation);
};

export const selectConnectDefaultScale = (state: RootState): SsConnectScale | undefined => selectDefaultScale(get(state, 'ssconnect'));
export const selectConnectFlattenedPrinters = (state: RootState): SsConnectPrinter[] => selectFlattenedPrinters(get(state, 'ssconnect'));
export const selectConnectHasConnect = (state: RootState): boolean => selectHasConnect(get(state, 'ssconnect'));
export const selectConnectPrintJobs = (state: RootState): SsConnectPrintJob[] => selectPrintJobs(get(state, 'ssconnect'));
export const selectConnectPrinters = (state: RootState): SsConnectPrinterGroup[] => selectPrinters(get(state, 'ssconnect'));
export const selectConnectScales = (state: RootState): SsConnectScaleGroup[] => selectScales(get(state, 'ssconnect'));
export const selectConnectWorkstationById = (state: RootState, props: { workstationId: string }): SsConnectWorkstation =>
  selectWorkstationById(get(state, 'ssconnect'), props);
export const selectConnectWorkstations = (state: RootState): SsConnectWorkstation[] => selectWorkstations(get(state, 'ssconnect'));
